import React, { useState, useMemo, useRef, useEffect } from 'react';

function tintFor(conf) {
  if (conf == null) return '#FFFFFF';
  if (conf >= 0.8) return '#E8F5E9';
  if (conf >= 0.6) return '#F1FBF3';
  if (conf >= 0.4) return '#FFF9E6';
  return '#FFEBEE';
}

function SignalBars({ conf }) {
  const filledCount = conf == null ? 0 : Math.max(1, Math.min(5, Math.round(conf * 5)));
  const title = conf == null ? 'no confidence' : `confidence ${Math.round(conf * 100)}%`;
  return (
    <div title={title} style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2 }}>
      {[...Array(5)].map((_, i) => {
        const filled = i < filledCount;
        return (
          <div key={i} style={{ width: 3, height: 10, borderRadius: 1, background: filled ? '#3D85E6' : '#DADCE0' }} />
        );
      })}
    </div>
  );
}

export default function MatrixView({
  rows = [],
  columns = [],
  cells = {},
  selected = new Set(),
  onToggleSelect = () => {},
  onOpenInAnalysis = () => {},
  initialVisible
}) {
  const [visible, setVisible] = useState([]);
  const [sort, setSort] = useState(null);
  const [menu, setMenu] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [colWidths, setColWidths] = useState({});
  const wrapperRef = useRef(null);
  const thRefs = useRef({});
  const dragRef = useRef(null);

  useEffect(() => {
    if (initialVisible) {
      setVisible(initialVisible);
    } else {
      const nonSys = columns.filter(c => !c.system).slice(0, 8).map(c => c.name);
      setVisible(nonSys.length > 0 ? nonSys : columns.map(c => c.name));
    }
  }, [columns, initialVisible]);

  useEffect(() => {
    const handleDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMenu(null);
        setPickerOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setMenu(null);
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const visibleColumns = useMemo(() => {
    return visible.map(n => columns.find(c => c.name === n)).filter(Boolean);
  }, [visible, columns]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const sorted = [...rows];
    const t = k => {
      const cellKey = k + ' ' + sort.name;
      return cells[cellKey]?.text || '';
    };
    sorted.sort((a, b) => {
      const valA = t(a.key);
      const valB = t(b.key);
      const cmp = valA.localeCompare(valB, undefined, { numeric: true });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [rows, sort, cells]);

  const toggleColumn = (name) => {
    let next;
    if (visible.includes(name)) {
      next = visible.filter(n => n !== name);
    } else {
      const set = new Set([...visible, name]);
      next = columns.filter(c => set.has(c.name)).map(c => c.name);
    }
    setVisible(next);
  };

  const handleCaretClick = (e, name) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ name, x: rect.left, y: rect.bottom });
  };

  const handleCopyColumn = (colName) => {
    const text = sortedRows.map(r => cells[r.key + ' ' + colName]?.text || '').join('\n');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  const handleResizeStart = (e, name) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths[name] ?? thRefs.current[name]?.getBoundingClientRect().width ?? 120;
    dragRef.current = { name, startX, startWidth };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const { name: n, startX: sx, startWidth: sw } = dragRef.current;
      const w = Math.max(48, sw + (ev.clientX - sx));
      setColWidths(prev => ({ ...prev, [n]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const menuItemStyle = {
    width: '100%',
    textAlign: 'left',
    padding: '8px 14px',
    fontSize: 13,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'block'
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        fontFamily: 'var(--font-family-primary)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        position: 'relative'
      }}
    >
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 0', position: 'relative' }}>
        <div style={{ fontSize: 12, color: '#5F6368' }}>
          Matrix · {rows.length} takes × {visibleColumns.length} questions
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setPickerOpen(!pickerOpen)}
            style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #DADCE0', borderRadius: 4, background: '#FFFFFF', cursor: 'pointer' }}
          >
            + Columns
          </button>
          {pickerOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, maxHeight: 280, overflow: 'auto', background: '#FFFFFF', border: '1px solid #DADCE0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 8, minWidth: 220, zIndex: 60 }}>
              <div style={{ fontWeight: 'bold', fontSize: 11, color: '#5F6368', padding: '4px 0' }}>Questions</div>
              {columns.filter(c => !c.system).map(c => (
                <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={visible.includes(c.name)} onChange={() => toggleColumn(c.name)} />
                  {c.label || c.name}
                </label>
              ))}
              <div style={{ fontWeight: 'bold', fontSize: 11, color: '#5F6368', padding: '8px 0 4px 0' }}>Survey health</div>
              {columns.filter(c => c.system).map(c => (
                <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={visible.includes(c.name)} onChange={() => toggleColumn(c.name)} />
                  {c.label || c.name}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #E8EAED', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, top: 0, zIndex: 3, background: '#F8F9FA', minWidth: 200, textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #E8EAED', borderRight: '1px solid #E8EAED' }}>
                Recipient
              </th>
              {visibleColumns.map(col => (
                <th
                  key={col.name}
                  ref={el => { thRefs.current[col.name] = el; }}
                  onContextMenu={(e) => { e.preventDefault(); setMenu({ name: col.name, x: e.clientX, y: e.clientY }); }}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#F8F9FA',
                    padding: '8px 10px',
                    borderBottom: '1px solid #E8EAED',
                    whiteSpace: 'nowrap',
                    minWidth: 120,
                    cursor: 'default',
                    textAlign: 'left',
                    width: colWidths[col.name]
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.label || col.name}</span>
                    <button
                      onClick={(e) => handleCaretClick(e, col.name)}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px', fontSize: 11, color: '#5F6368' }}
                    >
                      ▾
                    </button>
                  </div>
                  <div
                    onMouseDown={(e) => handleResizeStart(e, col.name)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 6,
                      height: '100%',
                      cursor: 'col-resize',
                      userSelect: 'none'
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} style={{ padding: '20px', textAlign: 'center', color: '#5F6368' }}>
                  No takes yet.
                </td>
              </tr>
            ) : visibleColumns.length === 0 ? (
              <tr>
                <td colSpan={1} style={{ padding: '20px', textAlign: 'center', color: '#5F6368' }}>
                  No columns selected — use + Columns.
                </td>
              </tr>
            ) : (
              sortedRows.map(row => {
                const isSelected = selected.has(row.key);
                return (
                  <tr
                    key={row.key}
                    onClick={() => onToggleSelect(row.key)}
                    style={{ background: isSelected ? '#E3F2FD' : '#FFFFFF', cursor: 'pointer' }}
                  >
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: isSelected ? '#E3F2FD' : '#FFFFFF', borderRight: '1px solid #E8EAED', padding: '6px 10px', minWidth: 200 }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => onToggleSelect(row.key)}
                          style={{ marginRight: 8, cursor: 'pointer' }}
                        />
                        <div>
                          <div style={{ fontWeight: 500 }}>{row.handle}</div>
                          <div style={{ fontSize: 11, color: '#5F6368' }}>{row.timestamp} {row.status ? `· ${row.status}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    {visibleColumns.map(col => {
                      const cellKey = row.key + ' ' + col.name;
                      const cell = cells[cellKey];
                      const text = cell?.text;
                      const hasText = text !== undefined && text !== null && text !== '';
                      return (
                        <td
                          key={col.name}
                          style={{
                            background: tintFor(cell?.confidence),
                            padding: '6px 10px',
                            borderBottom: '1px solid #F1F3F4',
                            verticalAlign: 'top',
                            minWidth: 120,
                            width: colWidths[col.name]
                          }}
                        >
                          <div style={{ color: '#202124', wordBreak: 'break-word', maxWidth: 220 }}>
                            {hasText ? text : '—'}
                          </div>
                          {cell?.confidence != null && (
                            <div style={{ marginTop: 3 }}>
                              <SignalBars conf={cell.confidence} />
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {menu && (
        <div
          style={{ position: 'fixed', left: menu.x, top: menu.y, zIndex: 100, background: '#FFFFFF', border: '1px solid #DADCE0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 180, padding: '4px 0' }}
        >
          <button onClick={() => { onOpenInAnalysis?.(menu.name); setMenu(null); }} style={menuItemStyle}>Open in Analysis ↗</button>
          <button onClick={() => { setSort({ name: menu.name, dir: 'asc' }); setMenu(null); }} style={menuItemStyle}>Sort ascending</button>
          <button onClick={() => { setSort({ name: menu.name, dir: 'desc' }); setMenu(null); }} style={menuItemStyle}>Sort descending</button>
          <button onClick={() => { handleCopyColumn(menu.name); setMenu(null); }} style={menuItemStyle}>Copy column</button>
          <button onClick={() => { setVisible(visible.filter(n => n !== menu.name)); setMenu(null); }} style={menuItemStyle}>Hide column</button>
        </div>
      )}
    </div>
  );
}
